import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdArrowBack, MdSave, MdAdd } from 'react-icons/md';
import './AddProduct.css';
import Swal from 'sweetalert2';
import axios from 'axios';
import Select from 'react-select';
import {
    CREATE_PRODUCT_API,
    GET_ALL_PRODUCT_CATEGORIES_API,
    GET_ALL_UNITS_API,
    CREATE_PRODUCT_CATEGORY_API,
    CREATE_UNIT_API
} from '../config/API';
import Loader from '../components/common/Loader';
import Modal from '../components/common/Modal';
import PaginatedSelect from '../components/common/PaginatedSelect';
import { showErrorToast } from '../utils/toastUtils';

const AddProduct = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [fetchingCategories, setFetchingCategories] = useState(false);

    // Form State
    const [formData, setFormData] = useState({
        productName: '',
        description: '',
        size: '',
        defaultRate: '',
        productCategoryId: '',
        unit: ''
    });

    // Options State
    // Options are handled by PaginatedSelect
    const [dropdownCacheUniq, setDropdownCacheUniq] = useState(Date.now());

    // Quick Add Modal State
    const [activeModal, setActiveModal] = useState(null); // 'category', 'unit'
    const [quickAddFormData, setQuickAddFormData] = useState({});
    const [modalLoading, setModalLoading] = useState(false);

    const refreshDropdowns = () => {
        setDropdownCacheUniq(Date.now());
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleCategoryChange = (selectedOption) => {
        const val = selectedOption ? selectedOption.value : '';
        const unitVal = selectedOption ? selectedOption.unitId : '';

        setFormData(prev => ({
            ...prev,
            productCategoryId: val,
            categoryName: selectedOption ? selectedOption.label : '',
            unit: unitVal || prev.unit,
            unitName: selectedOption ? (selectedOption.unitName || prev.unitName) : prev.unitName
        }));
    };

    const handleUnitChange = (selectedOption) => {
        const val = selectedOption ? selectedOption.value : '';
        setFormData(prev => ({
            ...prev,
            unit: val,
            unitName: selectedOption ? selectedOption.label : ''
        }));
    };

    // --- Quick Add Handlers ---
    const openCategoryModal = () => {
        setQuickAddFormData({ categoryName: '', unitId: null });
        setActiveModal('category');
    };

    const openUnitModal = () => {
        setQuickAddFormData({ unitName: '' });
        setActiveModal('unit');
    };

    const handleQuickAddChange = (e) => {
        const { name, value } = e.target;
        setQuickAddFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleQuickAddSelectChange = (selected, field) => {
        setQuickAddFormData(prev => ({ ...prev, [field]: selected }));
    };
    const handleQuickAddSubmit = async () => {
        const token = localStorage.getItem('accessToken');
        let apiEndpoint = '';
        let payload = {};
        let successMsg = '';

        if (activeModal === 'category') {
            if (!quickAddFormData.categoryName) return showErrorToast('Category name is required');
            apiEndpoint = CREATE_PRODUCT_CATEGORY_API;
            payload = {
                categoryName: quickAddFormData.categoryName,
                status: 'active'
            };
            successMsg = 'Category created successfully';
        } else if (activeModal === 'unit') {
            if (!quickAddFormData.unitName) return showErrorToast('Unit name is required');
            apiEndpoint = CREATE_UNIT_API;
            payload = {
                unitName: quickAddFormData.unitName,
                status: 'active'
            };
            successMsg = 'Unit created successfully';
        }

        try {
            setModalLoading(true);
            const res = await axios.post(apiEndpoint, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (res.data.status) {
                Swal.fire('Success', successMsg, 'success', {
                    timer: 2000,
                    showConfirmButton: false,
                });
                const newData = res.data.data;
                refreshDropdowns();
                if (activeModal === 'category') {
                    setFormData(prev => ({
                        ...prev,
                        productCategoryId: newData._id || newData.id,
                        categoryName: newData.categoryName,
                        unit: newData.unitId || prev.unit,
                        unitName: newData.Unit?.unitName || prev.unitName
                    }));
                } else {
                    setFormData(prev => ({
                        ...prev,
                        unit: newData._id || newData.id,
                        unitName: newData.unitName
                    }));
                }
                setActiveModal(null);
            } else {
                showErrorToast(res.data.message || 'Failed to create');
            }
        } catch (error) {
            console.error('Quick create error:', error);
            showErrorToast('Error creating new entry');
        } finally {
            setModalLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const result = await Swal.fire({
            title: 'Save Product?',
            text: 'Do you want to save this product?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, Save',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#4f46e5',
            buttonsStyling: false,
            customClass: {
                confirmButton: 'swal-confirm-btn',
                cancelButton: 'swal-cancel-btn',
            },
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                const token = localStorage.getItem('accessToken');
                const payload = {
                    ...formData,
                    unitId: formData.unit, // Backend expects unitId
                    status: 'active'
                };
                const response = await axios.post(CREATE_PRODUCT_API, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.status) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Saved!',
                        text: 'Product created successfully',
                        timer: 2000,
                        showConfirmButton: false,
                    });
                    navigate('/products');
                } else {
                    showErrorToast(response.data.error || response.data.message || 'Failed to create product');
                }
            } catch (error) {
                console.error('Error creating product:', error);
                const errorData = error.response?.data?.errors || error.response?.data?.error?.[0] || error.response?.data?.message || 'An error occurred';
                showErrorToast(errorData);
            } finally {
                setLoading(false);
            }
        };
    }

    if (loading) {
        return <Loader fullPage text="Creating product..." />;
    }

    return (
        <div className="add-product-page">
            <div className="page-header">
                <button onClick={() => navigate('/products')} className="back-btn">
                    <MdArrowBack />
                </button>
                <h2>Add New Product</h2>
            </div>

            <form onSubmit={handleSubmit} className="product-form">
                <div className="form-section">
                    <h3>Product Information</h3>
                    <div className="form-grid">
                        <div className="form-group full-width">
                            <label>Product Name <span className="required-star">*</span></label>
                            <input
                                type="text"
                                name="productName"
                                value={formData.productName}
                                onChange={handleChange}
                                placeholder="Enter product name"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Category <span className="required-star">*</span></label>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <PaginatedSelect
                                    apiUrl={GET_ALL_PRODUCT_CATEGORIES_API}
                                    mapResponse={(cat) => ({
                                        value: cat._id || cat.id,
                                        label: cat.categoryName,
                                        unitId: cat.unitId || cat.Unit?._id || cat.Unit?.id || ''
                                    })}
                                    value={formData.productCategoryId ? { value: formData.productCategoryId, label: formData.categoryName || 'Selected Category' } : null}
                                    onChange={handleCategoryChange}
                                    placeholder="Select category"
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    isClearable
                                    cacheUniq={dropdownCacheUniq}
                                />
                                <button type="button" onClick={openCategoryModal} className="quick-add-btn" title="Add Category">
                                    <MdAdd size={20} />
                                </button>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Unit <span className="required-star">*</span></label>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <PaginatedSelect
                                    apiUrl={GET_ALL_UNITS_API}
                                    mapResponse={(u) => ({
                                        value: u._id || u.id,
                                        label: u.unitName
                                    })}
                                    value={formData.unit ? { value: formData.unit, label: formData.unitName || 'Selected Unit' } : null}
                                    onChange={handleUnitChange}
                                    placeholder="Select Unit"
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    isClearable
                                    cacheUniq={dropdownCacheUniq}
                                />
                                <button type="button" onClick={openUnitModal} className="quick-add-btn" title="Add Unit">
                                    <MdAdd size={20} />
                                </button>
                            </div>
                        </div>


                        <div className="form-group">
                            <label>Default Rate (₹)</label>
                            <input
                                type="number"
                                name="defaultRate"
                                value={formData.defaultRate}
                                onChange={handleChange}
                                placeholder="Enter amount"
                                min="0"
                            />
                        </div>

                        <div className="form-group full-width">
                            <label>Description</label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                rows="3"
                                placeholder="Enter product description"
                            ></textarea>
                        </div>
                    </div>
                </div>

                <div className="form-actions">
                    <button type="button" onClick={() => navigate('/products')} className="cancel-btn">
                        Cancel
                    </button>
                    <button type="submit" className="save-btn">
                        <MdSave /> Save Product
                    </button>
                </div>
            </form>

            {/* Quick Create Category Modal */}
            <Modal
                isOpen={activeModal === 'category'}
                onClose={() => setActiveModal(null)}
                title="Quick Create Category"
                footer={(
                    <div className="modal-footer">
                        <button className="swal-cancel-btn" onClick={() => setActiveModal(null)}>Cancel</button>
                        <button className="swal-confirm-btn" onClick={handleQuickAddSubmit} disabled={modalLoading}>
                            {modalLoading ? 'Creating...' : 'Create Category'}
                        </button>
                    </div>
                )}
            >
                <div className="form-grid" style={{ gridTemplateColumns: '1fr' }}>
                    <div className="form-group">
                        <label>Category Name <span className="required-star">*</span></label>
                        <input
                            type="text"
                            name="categoryName"
                            className="form-control"
                            value={quickAddFormData.categoryName || ''}
                            onChange={handleQuickAddChange}
                            placeholder="e.g. Electrical, Plumbing"
                        />
                    </div>
                </div>
            </Modal>

            {/* Quick Create Unit Modal */}
            <Modal
                isOpen={activeModal === 'unit'}
                onClose={() => setActiveModal(null)}
                title="Quick Create Unit"
                footer={(
                    <div className="modal-footer">
                        <button className="swal-cancel-btn" onClick={() => setActiveModal(null)}>Cancel</button>
                        <button className="swal-confirm-btn" onClick={handleQuickAddSubmit} disabled={modalLoading}>
                            {modalLoading ? 'Creating...' : 'Create Unit'}
                        </button>
                    </div>
                )}
            >
                <div className="form-group">
                    <label>Unit Name <span className="required-star">*</span></label>
                    <input
                        type="text"
                        name="unitName"
                        className="form-control"
                        value={quickAddFormData.unitName || ''}
                        onChange={handleQuickAddChange}
                        placeholder="e.g. Kg, Pkts, Box"
                    />
                </div>
            </Modal>
        </div>
    );
};

export default AddProduct;


